<template>
  <div class="message" ref="note">
    <div class="row mt-1 mb-1 internal">
      <div class="col mr--2">
        <div class="comment-body">
          <p class="comment-text">
            <span v-html="content" class="text-dark" @click="onContentClick"></span>
          </p>
          <p class="mt-1 mb-0 text-dark" v-if="ticketNumber && !ticketReference">
            <a v-if="ticketPath" href="#" @click.prevent="openTicket">
              View ticket #{{ ticketNumber }}
            </a>
            <span v-else>#{{ ticketNumber }}</span>
          </p>
          <div class="row" v-if="created">
            <div class="col-auto">
              <p class="comment-time text-dark">
                {{ created | moment('LT') }}
                {{ timezone }}
                <span v-if="location">
                  &#8226;
                  {{ location.friendlyName || location.city }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-auto mt-2">
        <Avatar
          :img="user.avatar"
          :first-name="user.firstName"
          :last-name="user.lastName"
          :name="user.firstName + ' ' + user.lastName"
          size="xs"
          status=""
        />
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/MainContent/Avatar'
import Config from '@/config'
import { findTicketReference, TICKET_REFERENCE_SENTINEL } from '@/lib/ticket-note-reference'

export default {
  name: 'NoteEvent',
  props: ['created', 'note', 'user', 'timezone', 'location', 'autoScroll'],
  components: {
    Avatar
  },
  mounted() {
    // Only the optimistic note just added by the current user (the temp event
    // rendered from Timeline) passes autoScroll. Scroll the timeline's own
    // container to the bottom so that note appears immediately, even when the
    // user was scrolled up. Scope the scroll to #timelineList; the bare
    // scrollIntoView() the historical code used walked up and scrolled every
    // ancestor (the iframe body), which pushed the reply form off-screen.
    // Historical and paginated notes pass no autoScroll and never scroll.
    if (!this.autoScroll) return
    this.$nextTick(() => {
      // Resolve the timeline from this note's own ancestors rather than
      // document.getElementById, which returns the first #timelineList in the
      // document (other views, e.g. TimelineBody, also use that id).
      const list = this.$refs.note?.closest('#timelineList')
      if (list) {
        list.scrollTop = list.scrollHeight
      }
    })
  },
  computed: {
    // The note's own text with its ticket reference, when it names one, swapped for a sentinel
    // before any mention markup is built around it.
    content() {
      const { text: rawText } = this.note.mediaParts.find((mp) => mp.type === 'text')
      const reference = this.ticketReference
      const text = reference
        ? rawText.slice(0, reference.start) +
          TICKET_REFERENCE_SENTINEL +
          rawText.slice(reference.end)
        : rawText
      return reference
        ? this.withMentions(text).replace(TICKET_REFERENCE_SENTINEL, this.ticketReferenceHtml)
        : this.withMentions(text)
    },
    // "Created ticket #482: Title" links its own `#482` inline (OVA-4047). Only a note carrying
    // the structured `ticket` ref is looked at, and only for that ticket's number, so a typed
    // "#45" never becomes a link. `null` keeps the separate "View ticket #N" line below.
    ticketReference() {
      const { ticket } = this.note
      if (!ticket) return null
      const part = this.note.mediaParts.find((mp) => mp.type === 'text')
      return findTicketReference(part && part.text, ticket.number)
    },
    // The number is digits only (`findTicketReference` matched it as such), so it is safe to
    // place into the v-html markup. Under the headless build it stays plain text.
    ticketReferenceHtml() {
      const label = `#${this.ticketNumber}`
      return this.ticketPath ? `<a href="#" data-ticket-link>${label}</a>` : label
    },
    ticketNumber() {
      const { ticket } = this.note
      return ticket ? ticket.number : null
    },
    // Navigation is the host app's: OWA's IframeView pushes an inbound { path }
    // onto its own router, so pentakill needs no /tickets route. The headless
    // build is the mobile webview, which has no such parent listening — there
    // the number stays plain text.
    ticketPath() {
      const { ticket } = this.note
      if (!ticket || process.env.NODE_ENV === 'headless') return null
      return `/tickets/${ticket.id}`
    }
  },
  methods: {
    // `@{<userId>}` placeholders swapped for the mentioned person, highlighted.
    withMentions(text) {
      const { mentions } = this.note
      if (mentions?.length) {
        const extrapolatedRawMentions = text.match(/{[^}]*/gm)
        const extrapolatedMentions = extrapolatedRawMentions.reduce((acc, curr) => {
          acc.push(curr.replace(/{/gm, ''))
          return acc
        }, [])
        const interpolatedText = extrapolatedMentions.reduce((acc, curr) => {
          const regex = new RegExp(`@{${curr}}`)
          return acc.replace(
            regex,
            '<span style="background-color: #fbe6b1; padding: 1px; border-radius: 3px;">' +
              '@' +
              mentions.find((m) => m.user === curr).atFallbackText +
              '</span>'
          )
        }, text)
        return interpolatedText
      } else {
        return text
      }
    },
    // The inline link lives inside v-html, where no Vue listener can be bound, so the click is
    // caught on the note text and matched to the link here.
    onContentClick(event) {
      const link =
        event.target && event.target.closest && event.target.closest('[data-ticket-link]')
      if (!link) return
      event.preventDefault()
      this.openTicket()
    },
    openTicket() {
      window.parent.postMessage({ path: this.ticketPath }, Config.baseAppUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
.comment-body {
  background: #fdf3d8 !important;
}
.comment-text {
  &:after {
    border-left-color: #fdf3d8 !important;
  }
}
</style>
